import React, { createContext, useState, useContext } from "react";
import Axios from "../utils/Axios";
import { useTranslation } from "react-i18next";
import { AuthenticationContext } from "./AuthenticationProvider";

interface ProfileInterface {
    profile: Profile;
    getProfile: () => Promise<Profile>;
    updateProfile: (data: any) => void;
    requestPasswordReset: (emailAddress: string) => Promise<void>;
    setNewPassword: (password: string) => Promise<void>;
    profileLoading: boolean;
}

interface Profile {
    uid: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    country: string;
    username: string;
    language: string;
}

export const ProfileContext = createContext<ProfileInterface>({
    profile: {} as Profile,
    getProfile: () => {
        throw new Error('getProfile() method not implemented');
    },
    updateProfile: (data: any) => {
        throw new Error('updateProfile() method not implemented');
    },
    requestPasswordReset: (emailAddress: string) => {
        throw new Error('requestPasswordReset() method not implemented');
    },
    setNewPassword: (password: string) => {
        throw new Error('setNewPassword() method not implemented');
    },
    profileLoading: false
});

function mapData(data: any) {
    return {
        uid: data['zuser_uid'],
        firstName: data['zuser_firstname'],
        lastName: data['zuser_lastname'],
        emailAddress: data['zuser_email'],
        phoneNumber: data['zuser_telno'],
        country: data['zuser_country'],
        username: data['zuser_login'],
        language: data['language']
    } as Profile;
}

export const ProfileProvider: React.FC = ({children}) => {

    const [profile, setProfile] = useState({} as Profile);
    const [profileLoading, setLoading] = useState(false);
    const {i18n} = useTranslation();
    const {setAdmin} = useContext(AuthenticationContext);

    const getProfile = () => {
        setLoading(true);
        const accessToken = localStorage.getItem('access_token');
        return Axios.get('/profile', {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            setProfile(mapData(res.data));
            setLoading(false);
            return mapData(res.data);
        }).catch((error) => {
            console.error(error);
            setLoading(false);
            return {} as Profile;
        });
    }

    const updateProfile = (data: any) => {
        setLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.patch('/profile', data, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            setProfile(mapData(res.data));
            if (res.data.language !== '') {
                i18n.changeLanguage(res.data.language.toLowerCase());
            }
            setLoading(false);
        }).catch((error) => {
            console.error(error);
            setLoading(false);
        })
    }

    const requestPasswordReset = (emailAddress: string) => {
        setLoading(true);
        return Axios.post('/request_password_reset', {email: emailAddress}).then((res) => {
            setLoading(false);
        }).catch((error) => {
            console.error(error);
            setLoading(false);
        })
    }

    const setNewPassword = (password: string) => {
        setLoading(true);
        const accessToken = localStorage.getItem('access_token');
        return Axios.post('/profile/set_password', {password: password}, {headers: {'Authorization': `Bearer ${accessToken}`} }).then((res) => {
            setAdmin(res.data.is_admin);
            localStorage.setItem('is_admin', res.data.is_admin);
            setLoading(false);
        }).catch((error) => {
            console.error(error);
            setLoading(false);
        })
    }

    return <ProfileContext.Provider value={{
        profile,
        getProfile,
        updateProfile,
        requestPasswordReset,
        setNewPassword,
        profileLoading,
    }}>{children}</ProfileContext.Provider>;
}



